// ============================================================
// SUPPLIERS DATA
// BuildPilot Distribution Division, partner suppliers, products & materials
// distributed to the BuildPilot builder network, developers and projects.
//
// Schema (all fields optional except slug, name, tagline, categories, tier):
//   - slug, name, tagline, description (long-form)
//   - categories: [string], slugs from CATEGORIES below
//   - tier: 'distributed' | 'backed' | 'partner'
//   - location, supplyAreas: [string], yearEstablished, featured
//   - logo, heroImage (full URL or local path; null = clean text card)
//   - tagline_subline (short builder-facing one-liner under H1)
//   - keyStats: [{ value, label }]  → 3-4 corporate metrics band
//   - products: [string]
//   - applications: [string], typical use cases on a build
//   - builderBenefits: [string], why this matters to builders
//   - certifications: [string]
//   - highlights: [string], why BuildPilot stocks it
//   - clients: [string], notable clients/projects (no fabrications)
//   - contact: { website, email, phone }
// ============================================================

export const CATEGORIES = [
  {
    slug: 'concrete-and-footings',
    name: 'Concrete & Footings',
    description: 'Premix concrete, slabs, footings, low-carbon mixes, precast and shotcrete.',
    icon: 'Building2',
  },
  {
    slug: 'stone-and-surfaces',
    name: 'Stone & Surfaces',
    description: 'Engineered and natural stone for benchtops, splashbacks, walls, floors and facades.',
    icon: 'Layers',
  },
  {
    slug: 'kitchens-and-appliances',
    name: 'Kitchens & Appliances',
    description: 'Cabinetry, joinery, benchtops and appliances for kitchens and butler\'s pantries.',
    icon: 'Utensils',
  },
  {
    slug: 'solar-and-batteries',
    name: 'Solar & Batteries',
    description: 'Residential and commercial solar PV, battery storage and inverters. CEC-accredited supply and install.',
    icon: 'Sun',
  },
  {
    slug: 'payments-and-protection',
    name: 'Payments & Protection',
    description: 'Construction payment-integrity and progress-payment platforms that keep committed project funds connected to the work.',
    icon: 'BadgeCheck',
  },
];

export const SUPPLIERS = [
  // ========================================================
  // Sustainable Concrete Group (SCG)
  // ========================================================
  {
    slug: 'sustainable-concrete-group',
    name: 'Sustainable Concrete Group',
    tagline:
      'The world\'s greenest concrete, engineered aggregates that replace virgin sand, cement and CO₂-heavy materials with patented circular-economy inputs.',
    tagline_subline:
      'Tender-winning ESG performance. Net-zero capable. Distributed through BuildPilot to Australian builders, civil contractors and developers.',
    description:
      'Sustainable Concrete Group (SCG) is the holding entity behind a portfolio of patented technologies that decarbonise concrete at industrial scale. Waste glass, waste plastic, desert sand and reclaimed industrial by-products are converted into engineered aggregates and cementitious replacements (Silica Ecofume, ZYMIX), displacing the most carbon-intensive ingredients in the world\'s most-used building material. IP-protected, and ready for commercial deployment. Distributed to BuildPilot\'s builder and developer network for residential, commercial, civil and infrastructure projects across Australia.',
    categories: ['concrete-and-footings'],
    tier: 'distributed',
    location: 'Australia, National Supply',
    supplyAreas: ['Greater Adelaide', 'Greater Sydney', 'Greater Melbourne', 'Greater Brisbane', 'Greater Perth', 'Regional NSW, VIC, QLD, SA'],
    yearEstablished: 2019,
    featured: true,
    logo: null,
    heroImage: 'https://sustainableconcretegroup.com.au/assets/hero-australia-QBMy51wy.jpg',
    keyStats: [
      { value: '5', label: 'Patented sustainable concrete technologies' },
      { value: '6,000+', label: 'People across the group' },
      { value: 'ISO 9001 · 14001 · 45001', label: 'Certified management systems' },
      { value: 'QBCC 719350', label: 'Licensed civil & residential concrete' },
    ],
    products: [
      'Low-carbon premix concrete (residential slabs, footings, suspended slabs)',
      'Net-zero engineered aggregate from recycled plastics',
      'Engineered sand & Silica Ecofume from contaminated waste glass',
      'ZYMIX cement replacement (reduces embodied CO₂)',
      'Patented Modern Method of Construction (reduces on-site concrete volume)',
      'Precast manufacturing & installation (high-rise, commercial, bridges)',
      'Shotcrete application & boom/line pump fleet',
      'Civil concrete construction (formwork, steel fixing, placement)',
      'Asphalt supply & installation (roads)',
    ],
    applications: [
      'Residential slabs & footings (single dwelling, KDR, multi-unit)',
      'Knock-down rebuild slabs requiring ESG documentation',
      'Boutique developer projects targeting Green Star / NABERS ratings',
      'Civil & road infrastructure tenders',
      'Tunnels, basements, retaining structures',
      'Bridges and large-scale precast',
      'High-rise commercial precast and core walls',
    ],
    builderBenefits: [
      'Tender-winning ESG metrics, the lowest embodied-carbon concrete available in market',
      'No site-method changes required, pours, finishes and cures like standard premix',
      'Carbon reporting included with every delivery (for client documentation, as published by SCG)',
      'National supply footprint, consistent product across multi-site builders',
      'Prequalified with Tier 1 contractors and government, paperwork is already in place',
      'Modern Method of Construction reduces concrete volume on-site (faster pours, lower trade hours)',
    ],
    certifications: [
      'ISO 9001, Quality Management',
      'ISO 14001, Environmental Management',
      'ISO 45001, Workplace Health & Safety',
      'QBCC 719350 (Residential, Commercial, Industrial & Civil Concrete)',
      'Local Buy Prequalified',
    ],
    clients: [
      'Queensland Government',
      'NSW Government',
      'Victorian State Government',
      'Government of WA',
      'Government of SA',
      'John Holland',
      'Downer',
      'Fulton Hogan',
      'Acciona',
      'Lendlease',
    ],
    highlights: [
      'Featured in The Australian Financial Review, news.com.au, Daily Telegraph, Herald Sun, Courier Mail',
      '5 patented IP-protected sustainable technologies validated at industrial scale',
      'Recycles previously-unrecyclable plastics into net-zero carbon aggregate',
      'Australian R&D, deployed globally through local partners',
      'Distributed through BuildPilot to residential, commercial and civil projects',
    ],
    contact: {
      website: 'https://sustainableconcretegroup.com.au',
    },
  },

  // ========================================================
  // Lamina Stone
  // ========================================================
  {
    slug: 'lamina-stone',
    name: 'Lamina Stone',
    tagline:
      'Ultra-thin natural stone panels, authentic marble, travertine and onyx engineered for walls, floors, furniture and facades.',
    tagline_subline:
      '5mm of real natural stone bonded to a 2mm structural backing. Lightweight, strong, and easier to install on standard substrates. Distributed through BuildPilot to Australian builders, architects and luxury developers.',
    description:
      'Lamina Stone Australia is the exclusive regional wholesaler of European-engineered ultra-thin natural stone panels. Each panel combines 5mm of authentic natural stone (marble, travertine, onyx) with a 2mm composite backing, lightweight, dimensionally stable, and easier to install on standard timber, steel or masonry substrates. Where traditional slab work is heavy, slow and limited to benchtops, Lamina opens up walls, floors, ceilings, facades, joinery doors and custom furniture to real natural stone. Distributed through BuildPilot to builders, architects and luxury developers seeking the look and authenticity of slab stone without the weight, installation complexity or structural compromise.',
    categories: ['stone-and-surfaces', 'kitchens-and-appliances'],
    tier: 'distributed',
    location: 'Australia & New Zealand, Exclusive Wholesaler',
    supplyAreas: ['Greater Adelaide', 'Greater Sydney', 'Greater Melbourne', 'Greater Brisbane', 'Greater Perth', 'New Zealand'],
    featured: true,
    logo: null,
    heroImage:
      'https://images.squarespace-cdn.com/content/v1/690a816cda72e672405fcc87/968580f8-b7b1-4e28-aaad-9f8665d72506/IMG_0420.JPG',
    videoIntroUrl: '/videos/lamina-stone-intro.mp4',
    videoPosterUrl: '/videos/lamina-stone-intro-poster.jpg',
    keyStats: [
      { value: '7mm', label: 'Total panel thickness (5mm stone + 2mm backing)' },
      { value: 'AU & NZ', label: 'Exclusive regional wholesaler' },
      { value: 'Marble · Travertine · Onyx', label: 'Authentic natural stone ranges' },
      { value: 'EU-engineered', label: 'European manufacturing partners' },
    ],
    products: [
      'Ultra-thin marble panels (Carrara, Calacatta, Statuario, more)',
      'Ultra-thin travertine panels (textured and honed finishes)',
      'Ultra-thin onyx panels (with backlit/luminous applications)',
      'Large-format wall cladding panels',
      'Floor tile applications (interior)',
      'Bench & vanity tops',
      'Joinery panels (cabinet doors, drawer fronts, feature inserts)',
      'Custom furniture pieces (tables, vanities, bath surrounds)',
      'External facade panels',
    ],
    applications: [
      'Kitchen splashbacks & feature walls (real stone, no joins)',
      'Vanity tops & full bathroom wraps (lightweight on stud walls)',
      'Custom joinery doors & drawer fronts',
      'Stair risers & treads',
      'Feature walls in living spaces',
      'Commercial fit-outs (hotels, restaurants, retail)',
      'Yacht & marine interiors (weight-critical)',
      'External cladding & rainscreen facades',
      'Bespoke furniture (entry tables, plinths, monolithic forms)',
    ],
    builderBenefits: [
      'Installs on standard substrates, timber, MDF, plywood, plasterboard, masonry. No specialist trades required',
      'Roughly 1/5th the weight of traditional slab, handled by 1-2 trades instead of stone-rigging crews',
      'Cut on-site with standard tile/timber tooling, faster, fewer transit chips, less waste',
      'Reduces structural over-engineering, no need to upsize joists, brackets or wall framing for stone weight',
      'Real natural stone authenticity, every panel unique, no engineered repetition',
      'Project support included, Lamina works directly with builders, architects and designers on specification',
    ],
    certifications: [],
    clients: [],
    highlights: [
      'Exclusive AU & NZ wholesaler, backed supply, no parallel-import risk',
      'European manufacturing, engineered for dimensional stability and consistent yield',
      'Authentic natural stone, not engineered/reconstituted stone',
      'Lightweight construction opens up applications that traditional stone cannot',
      'Direct technical support for builders and architects on specification',
      'Distributed through BuildPilot to AU partner builders and luxury developers',
    ],
    contact: {
      website: 'https://www.laminastone.com.au',
    },
  },

  // ========================================================
  // Best Solar and Batteries (Adelaide)
  // ========================================================
  {
    slug: 'best-solar-and-batteries',
    name: 'Best Solar and Batteries',
    tagline:
      'South Australia\'s in-house solar PV and battery storage specialist. CEC-accredited design, supply and install for new homes, knockdown rebuilds and commercial projects.',
    tagline_subline:
      '3,000+ South Australian installs. CEC-accredited installers. NETCC-approved seller. Distributed through BuildPilot to AU partner builders, KDR clients and commercial developers.',
    description:
      'Best Solar and Batteries is South Australia\'s in-house solar PV, battery storage and inverter specialist - proudly SA owned and operated, with a CEC-accredited installation team and 250+ years of combined experience across the SA residential and commercial market. Every step is handled in-house: site assessment, custom system design, supply of premium panels/inverters/batteries, install, SAPN Small Embedded Generation (SEG) approval and bi-directional meter coordination. They are an NETCC (New Energy Tech Consumer Code) approved seller and a Solar Accreditation Australia member, and they hold SA building licence BLD 322297. Distributed through BuildPilot to partner builders, KDR clients, developers and commercial projects that need solar specified at design stage (not retro-fitted as an afterthought).',
    categories: ['solar-and-batteries'],
    tier: 'distributed',
    location: 'Adelaide, South Australia',
    supplyAreas: ['Greater Adelaide', 'Adelaide Hills', 'Fleurieu Peninsula', 'Barossa', 'Regional SA'],
    yearEstablished: null,
    featured: true,
    logo: null,
    heroImage:
      'https://cdn.prod.website-files.com/6350d1746065cf3df6e74753/635a1251e448c95b6096f14a_i-7wzFqNp-4K%202%20(1).jpg',
    keyStats: [
      { value: '3,000+', label: 'South Australian customers' },
      { value: '65+', label: 'In-house team members' },
      { value: '250+ yrs', label: 'Combined solar industry experience' },
      { value: 'CEC · NETCC · SAA', label: 'Accredited & approved seller' },
    ],
    products: [
      'Residential solar panel systems (Tier 1 panels, custom roof layout)',
      'Commercial solar panel systems (warehouses, offices, agribusiness)',
      'Residential battery storage (Tesla Powerwall, LG, AlphaESS, Sungrow, SigEnergy)',
      'Commercial battery storage and load-shifting systems',
      'Hybrid and string inverters (Goodwe, Fronius, SolarEdge, Solis)',
      'EV-ready DC and AC chargers integrated with the home PV system',
      'Off-grid and partial off-grid solutions',
      'Monitoring portal setup and ongoing system performance support',
      'SAPN Small Embedded Generation (SEG) approval coordination',
      'Bi-directional meter installation / re-programming coordination',
    ],
    applications: [
      'New build solar specified at design stage (panels, inverter, battery sized to the home)',
      'Knockdown rebuild PV/battery packages integrated with the slab and roof brief',
      'House and land package upgrades (replace developer-standard system with premium kit)',
      'Off-grid Hills/regional builds where mains connection is cost-prohibitive',
      'EV-ready homes with charger circuits roughed in pre-plaster',
      'Commercial PV for warehouses, offices, retail, agribusiness',
      'Battery retrofit to existing solar systems with under-spec\'d storage',
      'NABERS / Green Star target projects requiring documented PV capacity',
    ],
    builderBenefits: [
      'Single supplier from design to commissioning - one quote, one point of contact, no sub-contractor finger-pointing',
      'CEC-accredited in-house installers (not labour-hire) - quality and SAPN compliance ride on the same team that designed the system',
      'NETCC-approved seller and Solar Accreditation Australia member - paperwork your client\'s lender or council will already accept',
      'SAPN SEG approval and bi-directional meter coordination handled end-to-end - no builder-side admin',
      'Premium-only panel/inverter/battery brands - JA Solar, Q-Cell, Risen, Jinko, REC, Goodwe, Fronius, SolarEdge, Solis, Tesla, LG, AlphaESS, Sungrow, SigEnergy',
      'Pricing locked at quote - no scope creep mid-build',
      'humm finance available for the homeowner - reduces builder finance pressure',
      'Long-term local team for warranty and maintenance - they are still in SA in five years',
    ],
    certifications: [
      'Clean Energy Council (CEC) accredited installers and designers',
      'NETCC (New Energy Tech Consumer Code) approved seller',
      'Solar Accreditation Australia member',
      'SA building licence BLD 322297',
    ],
    clients: [],
    highlights: [
      '3,000+ South Australian installs across residential and commercial',
      '100% in-house team - design, supply, install and after-sales are the same crew',
      'Premium-tier brand panel / inverter / battery products only',
      'SAPN SEG approval and bi-directional meter coordination handled in-house',
      'NETCC-approved seller (the federal-government-endorsed consumer-protection code for new-energy tech)',
      'Available to BuildPilot partner builders for solar specified at design stage on every new build or KDR',
    ],
    contact: {
      website: 'https://www.bestsolarandbatteries.com',
      phone: '1800 945 192',
    },
  },
  // ========================================================
  // SolarQuotes (national solar / battery / EV quote service)
  // ========================================================
  {
    slug: 'solarquotes',
    name: 'SolarQuotes',
    tagline:
      'Independent Australian quote service connecting homeowners with up to three vetted local installers for solar, batteries, hot-water heat pumps and EV chargers. Running since 2009.',
    tagline_subline:
      '1M+ Australians served. 100,000+ uncensored installer reviews. Good Installer Guarantee. Available Australia-wide to BuildPilot new-build, KDR and renovation clients.',
    description:
      'SolarQuotes is an independent Australian service that helps homeowners electrify their homes with confidence. Founded in 2009 by chartered electrical engineer Finn Peacock, it connects you with up to three pre-vetted local installers for solar panels, home batteries, hot-water heat pumps and EV chargers - so you can compare genuine quotes side by side instead of chasing installers one at a time. For a new build or knockdown rebuild, getting quotes early means solar, battery and EV-charger provisioning can be designed in before plasterboard goes up, not retro-fitted later. BuildPilot introduces the service and earns a small referral fee when you request quotes, at no extra cost to you.',
    categories: ['solar-and-batteries'],
    tier: 'partner',
    location: 'Australia-wide',
    supplyAreas: ['Australia-wide'],
    yearEstablished: 2009,
    featured: true,
    logo: '/partners/solarquotes/solarquotes-logo.webp',
    heroImage:
      'https://images.unsplash.com/photo-1786515284601-f9ac47d0758a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600',
    keyStats: [
      { value: '1M+', label: 'Australians served since 2009' },
      { value: 'Up to 3', label: 'Free quotes from vetted installers' },
      { value: '100,000+', label: 'Uncensored installer reviews published' },
      { value: 'Australia-wide', label: 'National installer network' },
    ],
    products: [
      'Up to three free quotes for residential solar panel systems',
      'Home battery storage quotes (new install and add-on to existing solar)',
      'Hot-water heat pump quotes',
      'EV charger quotes',
      'Commercial solar quotes',
      'Independent guides, research and uncensored installer reviews',
    ],
    applications: [
      'New build solar + battery sized to the home and designed in before handover',
      'Knockdown rebuild PV / battery / EV-charger provisioning at design stage',
      'Comparing installers for a renovation or existing-home solar upgrade',
      'Adding a battery to an existing solar system',
      'Hot-water heat pump upgrades for electrification',
    ],
    builderBenefits: [
      'Gives new-build and KDR clients three comparable quotes without the builder fielding solar enquiries',
      'Pre-vetted installer network and Good Installer Guarantee reduce warranty and quality risk',
      'Independent, unbiased positioning your clients already trust',
      'Covers the full electrification stack: solar, battery, hot-water heat pump and EV charger',
      'National coverage, so it works for clients in every state',
    ],
    certifications: [],
    clients: [],
    highlights: [
      'Independent and unbiased, founded 2009 by chartered electrical engineer Finn Peacock',
      'Up to three free quotes from pre-vetted local installers',
      '100,000+ uncensored customer reviews of solar and home-energy installers',
      'Good Installer Guarantee across solar, batteries, EV chargers and heat pumps',
      'Best requested early so energy provisioning is designed into the build, not retro-fitted',
    ],
    contact: {
      website: 'https://www.solarquotes.com.au/?utm_source=bpi',
    },
  },
  // ========================================================
  // PayLocker (construction payment integrity)
  // ========================================================
  {
    slug: 'paylocker',
    name: 'PayLocker',
    tagline:
      'Australian construction payment-integrity platform. Committed project funds are held in a project-specific account and released against approved progress and verified claims.',
    tagline_subline:
      'Verify, stage, fund, approve, release. Distributed through BuildPilot to homeowners, builders, trades and suppliers. Coming soon, register your interest now.',
    description:
      'PayLocker keeps construction money connected to the project it was paid for. Instead of a progress payment landing in a general account, committed funds enter a project-specific pathway and are released through a controlled process tied to verified claims and approved progress. Every participant is identified by role, licence, ABN and nominated account, approvals are recorded as visible steps, and a clear payment history is kept. PayLocker is not a bank, insurer, accounting tool or project-management tool, and it does not replace your building contract or statutory home-warranty insurance. It is an awareness and register-interest launch on BuildPilot: PayLocker handles qualification, onboarding, customer agreements, payment services and ongoing support.',
    categories: ['payments-and-protection'],
    tier: 'distributed',
    location: 'Australia',
    supplyAreas: ['Australia-wide'],
    yearEstablished: null,
    featured: true,
    logo: '/images/partners/paylocker-icon.png',
    heroImage: null,
    keyStats: [
      { value: '5 steps', label: 'Verify, stage, fund, approve, release' },
      { value: 'Project-specific', label: 'Funds stay connected to the build' },
      { value: 'Recorded', label: 'Visible approval and payment history' },
      { value: 'Australia-wide', label: 'National platform' },
    ],
    products: [
      'Project-specific construction payment account',
      'Participant verification (role, licence, ABN, nominated account)',
      'Stage-based funding aligned to the building contract',
      'Claim submission and recorded approvals against the work',
      'Controlled release of approved payments in the agreed sequence',
      'Visible payment history for homeowners, builders, trades and suppliers',
    ],
    applications: [
      'New home builds where the homeowner wants payment transparency from the start',
      'Builders offering project-specific payment integrity as a differentiator',
      'Trades and suppliers seeking certainty they are paid against approved work',
      'Developers and principals coordinating multi-party payments',
    ],
    builderBenefits: [
      'Keep control of the build without being the project\'s informal bank',
      'Offer clients project-specific payment transparency at quote stage',
      'Claims tie to stages and evidence; approvals and releases are visible and recorded',
      'Verified participants are paid in the approved sequence once funds are available',
    ],
    certifications: [],
    clients: [],
    highlights: [
      'Committed funds stay connected to the project, not a general operating account',
      'Five-step pathway: verify, stage, fund, approve, release',
      'Clear, recorded approval and payment history for every party',
      'Complements your building contract and statutory insurance, it does not replace them',
      'Register your interest on BuildPilot ahead of launch',
    ],
    contact: {
      website: 'https://www.paylocker.com.au',
      phone: '03 7050 6080',
    },
  },

  // ========================================================
  // Kitchen & appliance affiliate retailers (NOT partners).
  // Lightweight cards that surface the brands under the
  // Kitchens & Appliances category; each links to its affiliate
  // profile at /suppliers/<slug> (Retail / Affiliate badge).
  // ========================================================
  {
    slug: 'tefal',
    name: 'Tefal',
    tagline:
      'Non-stick cookware and everyday kitchen appliances - frypans, cookware sets, air fryers, kettles and more, delivered Australia-wide.',
    categories: ['kitchens-and-appliances'],
    tier: 'affiliate',
    location: 'Australia-wide',
    logo: '/images/suppliers/tefal-logo.png',
    contact: { website: 'https://www.tefal.com.au' },
  },
  {
    slug: 'kenwood',
    name: 'Kenwood',
    tagline:
      'Stand mixers, food processors and kitchen machines to kit out a new or renovated kitchen, shipped Australia-wide.',
    categories: ['kitchens-and-appliances'],
    tier: 'affiliate',
    location: 'Australia-wide',
    logo: '/images/suppliers/kenwood-logo.png',
    contact: { website: 'https://www.kenwoodworld.com/en-au' },
  },
  {
    slug: 'braun',
    name: 'Braun Household',
    tagline:
      'German-engineered benchtop kitchen appliances and garment care - blenders, air fryers, kettles, toasters and steam irons.',
    categories: ['kitchens-and-appliances'],
    tier: 'affiliate',
    location: 'Australia-wide',
    logo: '/images/suppliers/braun-logo.png',
    contact: { website: 'https://www.braunhousehold.com/en-au' },
  },
];

// ============================================================
// Helper functions
// ============================================================
export const getSupplierBySlug = (slug) => SUPPLIERS.find((s) => s.slug === slug);

export const getSuppliersByCategory = (categorySlug) =>
  SUPPLIERS.filter((s) => s.categories.includes(categorySlug));

export const getFeaturedSuppliers = () => SUPPLIERS.filter((s) => s.featured);

export const getCategoryBySlug = (slug) => CATEGORIES.find((c) => c.slug === slug);

export const tierLabel = (tier) => {
  switch (tier) {
    case 'backed':
      return 'BuildPilot Backed';
    case 'distributed':
      return 'BuildPilot Distributed';
    case 'partner':
      return 'Partner Supplier';
    case 'affiliate':
      return 'Retail / Affiliate';
    default:
      return 'Partner Supplier';
  }
};
